import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { money } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Minus, Plus, Trash2 } from "lucide-react";

export default function NewOrderDialog({ open, onOpenChange, onCreated }) {
  const { t, lang, localized } = useI18n();
  const [tables, setTables] = useState([]);
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [cart, setCart] = useState([]); // {menu_item_id, qty, name, price}
  const [tableId, setTableId] = useState("");
  const [selectedCat, setSelectedCat] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!open) return;
    (async () => {
      const [tRes, cRes, iRes] = await Promise.all([
        api.get("/tables"),
        api.get("/categories"),
        api.get("/menu-items"),
      ]);
      setTables(tRes.data);
      setCategories(cRes.data);
      setItems(iRes.data);
      setCart([]);
      setTableId("");
    })();
  }, [open]);

  const filtered = items
    .filter((i) => i.available !== false)
    .filter((i) => (selectedCat === "all" ? true : i.category_id === selectedCat))
    .filter((i) => {
      if (!search) return true;
      const s = search.toLowerCase();
      return (localized(i.name) || "").toLowerCase().includes(s);
    });

  const add = (it) => {
    setCart((prev) => {
      const ex = prev.find((r) => r.menu_item_id === it.id);
      if (ex) return prev.map((r) => (r.menu_item_id === it.id ? { ...r, qty: r.qty + 1 } : r));
      return [...prev, { menu_item_id: it.id, qty: 1, name: localized(it.name), price: it.price }];
    });
  };
  const remove = (id) => setCart((p) => p.filter((r) => r.menu_item_id !== id));
  const dec = (id) => setCart((p) => p.map((r) => (r.menu_item_id === id ? { ...r, qty: Math.max(1, r.qty - 1) } : r)));

  const total = cart.reduce((s, r) => s + r.qty * r.price, 0);

  const submit = async () => {
    if (cart.length === 0) return toast.error(lang === "tr" ? "Sepet boş" : "Warenkorb leer");
    try {
      const { data } = await api.post("/orders", {
        table_id: tableId || null,
        items: cart.map((r) => ({ menu_item_id: r.menu_item_id, qty: r.qty })),
      });
      onOpenChange(false);
      onCreated?.(data.id);
      toast.success(t("save"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-display">{t("newOrder")}</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2 space-y-3">
            <div className="flex gap-2">
              <Select value={tableId || "none"} onValueChange={(v) => setTableId(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="order-table-select"><SelectValue placeholder={t("table")} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("noTable")}</SelectItem>
                  {tables.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name} ({t.status})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input placeholder={t("search")} value={search} onChange={(e) => setSearch(e.target.value)} data-testid="order-item-search" />
            </div>

            <div className="flex gap-2 overflow-x-auto">
              <ChipBtn active={selectedCat === "all"} onClick={() => setSelectedCat("all")}>
                {lang === "tr" ? "Tümü" : "Alle"}
              </ChipBtn>
              {categories.map((c) => (
                <ChipBtn key={c.id} active={selectedCat === c.id} onClick={() => setSelectedCat(c.id)}>
                  {localized(c.name)}
                </ChipBtn>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-2 max-h-80 overflow-y-auto pr-1">
              {filtered.map((it) => (
                <button
                  key={it.id}
                  onClick={() => add(it)}
                  data-testid={`add-menu-item-${it.id}`}
                  className="text-left p-3 rounded-md border hover:border-primary/60 hover:bg-muted/40 active:scale-95 transition-all"
                >
                  <div className="text-sm font-medium truncate">{localized(it.name)}</div>
                  <div className="text-xs font-mono text-primary mt-1">{money(it.price)}</div>
                </button>
              ))}
              {filtered.length === 0 && (
                <div className="col-span-2 text-center py-8 text-sm text-muted-foreground">{t("empty")}</div>
              )}
            </div>
          </div>

          <div className="border rounded-lg p-3 flex flex-col">
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">{lang === "tr" ? "Sepet" : "Warenkorb"}</div>
            <div className="flex-1 space-y-2 max-h-72 overflow-y-auto">
              {cart.length === 0 && <div className="text-sm text-muted-foreground py-6 text-center">{t("empty")}</div>}
              {cart.map((r) => (
                <div key={r.menu_item_id} className="flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{r.name}</div>
                    <div className="text-xs text-muted-foreground font-mono">{money(r.price)}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button size="icon" variant="ghost" onClick={() => dec(r.menu_item_id)}><Minus className="w-3 h-3" /></Button>
                    <span className="w-6 text-center font-mono text-sm">{r.qty}</span>
                    <Button size="icon" variant="ghost" onClick={() => add({ id: r.menu_item_id, name: { [lang]: r.name }, price: r.price })}><Plus className="w-3 h-3" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(r.menu_item_id)}><Trash2 className="w-3 h-3 text-destructive" /></Button>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 pt-3 border-t flex justify-between font-display font-semibold">
              <span>{t("total")}</span>
              <span className="font-mono" data-testid="new-order-total">{money(total)}</span>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("cancel")}</Button>
          <Button onClick={submit} data-testid="create-order-submit">{t("save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChipBtn({ active, children, onClick }) {
  return (
    <button onClick={onClick} className={`px-3 py-1 rounded-full text-xs border whitespace-nowrap ${active ? "bg-foreground text-background" : "bg-background"}`}>
      {children}
    </button>
  );
}
